import React, { useEffect, useState, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { makeRequestAuth } from "../../_config/api";
import { ToastContainer } from "react-toastify";
import {
  FiArrowLeft,
  FiPlus,
  FiFileText,
  FiTrash2,
  FiEdit2,
  FiHash,
  FiList,
  FiClock,
  FiBookOpen,
} from "react-icons/fi";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import NoData from "../../utils/DataNotFound";
import AddPaperModal from "./AddPaperModal";
import EditPaperModal from "./EditPaperModal";
import GuidelineModal from "./GuidelineModal";

export default function PaperList() {
  const { subjectId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const subjectName = location.state?.subjectName || "Subject";
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [showGuidelineModal, setShowGuidelineModal] = useState(null);

  const fetchPapers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await makeRequestAuth("post", "/admin/getPapersBySubject", {
        subjectId,
      });
      setPapers(res?.data || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [subjectId]);

  useEffect(() => {
    if (subjectId) fetchPapers();
  }, [subjectId, fetchPapers]);

  const handleDelete = async (paperId) => {
    if (!window.confirm("Are you sure?")) return;
    try {
      await makeRequestAuth("post", "/admin/deletePaper", { paperId });
      showSuccessToast("Paper deleted");
      fetchPapers();
    } catch {
      showErrorToast("Delete failed");
    }
  };

  // --- UPDATED NAVIGATION ---
  const handleViewQuestionList = (paper) => {
    navigate(`/admin/paper-details/${paper._id}`, {
      state: {
        paperName: paper.paperName || paper.name,
        totalQuestions: paper.totalQuestions,
      },
    });
  };

  return (
    <div className="p-4">
      <ToastContainer />
      <div className="flex flex-col sm:flex-row items-start justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-full transition"
          >
            <FiArrowLeft size={22} />
          </button>
          <div>
            <h2 className="text-2xl font-bold text-gray-800 capitalize">
              {subjectName} / Papers
            </h2>
            <p className="text-gray-500 text-sm">Manage papers</p>
          </div>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-5 py-2 rounded-lg shadow-md transition-all active:scale-95"
        >
          <FiPlus size={18} /> <span>Add Paper</span>
        </button>
      </div>

      {loading ? (
        <div className="p-8 text-center text-gray-500">Loading...</div>
      ) : papers.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-8 border">
          <NoData title="No Papers Found" />
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full text-left border-collapse hidden md:table">
            <thead className="bg-gray-50 text-gray-600 uppercase text-xs font-semibold border-b">
              <tr>
                <th className="px-6 py-4 w-16 text-center">#</th>
                <th className="px-6 py-4">Paper Name</th>
                <th className="px-6 py-4 text-center">Questions</th>
                <th className="px-6 py-4 text-center">Duration (Min.)</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {papers.map((paper, index) => (
                <tr
                  key={paper._id}
                  className="hover:bg-gray-50 transition-colors"
                >
                  <td className="px-6 py-4 text-center text-gray-500">
                    {index + 1}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="bg-teal-50 text-teal-600 p-2 rounded-lg">
                        <FiFileText />
                      </div>
                      <span className="font-medium text-gray-800">
                        {paper.paperName || paper.name}
                      </span>
                    </div>
                  </td>

                  <td className="px-6 py-4 text-center">
                    <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 px-2.5 py-1 rounded-full text-xs font-bold border border-gray-200">
                      <FiHash size={12} /> {paper.totalQuestions}
                    </span>
                  </td>

                  <td className="px-6 py-4 text-center">
                    {paper.durationMinutes ? (
                      <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 px-2.5 py-1 rounded-full text-xs font-bold border border-gray-200">
                        <FiClock size={12} /> {paper.durationMinutes}
                      </span>
                    ) : (
                      "-"
                    )}
                  </td>

                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => setShowGuidelineModal(paper)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-md"
                      >
                        <FiBookOpen size={14} />
                        {paper.guildelines
                          ? "View Guidelines"
                          : "Add Guidelines"}
                      </button>

                      <button
                        onClick={() => handleViewQuestionList(paper)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-md transition-colors mr-2"
                      >
                        <FiList size={14} /> Questions
                      </button>
                      <button
                        onClick={() => setEditData(paper)}
                        className="p-2 text-blue-600 bg-white border rounded-lg hover:bg-blue-50"
                      >
                        <FiEdit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(paper._id)}
                        className="p-2 text-red-600 bg-white border rounded-lg hover:bg-red-50"
                      >
                        <FiTrash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* Mobile View */}
          {/* Mobile View */}
          <div className="md:hidden divide-y divide-gray-100">
            {papers.map((paper, index) => (
              <div key={paper._id} className="p-4 flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-gray-400 text-sm font-mono">
                      {index + 1}.
                    </span>
                    <div>
                      <h4 className="font-semibold text-gray-800">
                        {paper.paperName || paper.name}
                      </h4>
                      <div className="flex gap-2 mt-1">
                        <span className="text-[10px] bg-gray-100 px-1.5 py-0.5 rounded border text-gray-600 font-bold">
                          {paper.totalQuestions} Qs
                        </span>
                        <span className="text-[10px] bg-gray-100 px-1.5 py-0.5 rounded border text-gray-600 font-bold">
                          {paper.durationMinutes || 0} Min
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Mobile Edit/Delete Buttons */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditData(paper)}
                      className="p-2 text-blue-600 bg-blue-50 rounded-lg border border-blue-100"
                    >
                      <FiEdit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(paper._id)}
                      className="p-2 text-red-600 bg-red-50 rounded-lg border border-red-100"
                    >
                      <FiTrash2 size={16} />
                    </button>
                  </div>
                </div>

                {/* Primary Action Buttons for Mobile */}
                <div className="flex gap-2 mt-1">
                  <button
                    onClick={() => setShowGuidelineModal(paper)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-md"
                  >
                    <FiBookOpen size={14} />
                    {paper.guildelines ? "View Guidelines" : "Add Guidelines"}
                  </button>

                  <button
                    onClick={() => handleViewQuestionList(paper)}
                    className="flex-1 py-2 text-[11px] font-bold text-purple-700 bg-purple-50 rounded-lg border border-purple-100 flex items-center justify-center gap-2"
                  >
                    <FiList size={14} /> Questions
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {showAddModal && (
        <AddPaperModal
          subjectId={subjectId}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            fetchPapers();
          }}
        />
      )}
      {editData && (
        <EditPaperModal
          data={editData}
          onClose={() => setEditData(null)}
          onSuccess={() => {
            setEditData(null);
            fetchPapers();
          }}
        />
      )}

      {showGuidelineModal && (
        <GuidelineModal
          data={showGuidelineModal}
          onClose={() => setShowGuidelineModal(null)}
          onSuccess={() => {
            setShowGuidelineModal(null);
            fetchPapers();
          }}
        />
      )}
    </div>
  );
}
